export type ErrorOrigin = 'server' | 'client';

/**
 * Critical application error that should be caught by error boundaries
 * and reported to monitoring services like Grafana Faro
 */
export class CriticalApplicationError extends Error {
  public readonly origin: ErrorOrigin;
  public readonly timestamp: string;
  public readonly innerError?: Error;

  constructor(message: string, origin: ErrorOrigin, innerError?: Error) {
    super(message);
    this.name = 'CriticalApplicationError';
    this.origin = origin;
    this.timestamp = new Date().toISOString();
    this.innerError = innerError;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CriticalApplicationError);
    }

    // If we have an inner error, preserve its stack
    if (innerError && innerError.stack) {
      this.stack = `${this.stack}\n\nInner Error: ${innerError.stack}`;
    }
  }
}

/**
 * Extension content scripts that inject into an *isolated* world never
 * surface to the host page's error reporting at all — invisible by browser
 * design, nothing to detect. This only catches the two ways an extension
 * DOES become visible to us: a main-world injected `<script>` referencing
 * its own extension:// URL in the stack, or (far more common) an extension
 * mutating the DOM out from under React, which throws when React's
 * reconciler tries to remove/move a node an extension already relocated —
 * a well-documented signature (Grammarly, password managers, ad blockers,
 * translation tools). Heuristic, not certain — a false positive here just
 * means a genuine DOM bug gets mislabeled, not lost; it's still reported
 * either way.
 */
const EXTENSION_PROTOCOL_PATTERN =
  /chrome-extension:\/\/|moz-extension:\/\/|safari-extension:\/\/|safari-web-extension:\/\//i;
const DOM_MUTATION_SIGNATURE_PATTERN =
  /removeChild|insertBefore|is not a child of this node|The object can not be found here/i;

export function isLikelyBrowserExtensionCrash(error: Error): boolean {
  return (
    DOM_MUTATION_SIGNATURE_PATTERN.test(error.message) ||
    EXTENSION_PROTOCOL_PATTERN.test(error.stack ?? '')
  );
}
